import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type AdminOrgFeature, type AdminOrgFeatures } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadError } from '@/components/ui/load-state';
import { formatDateTime } from '@/lib/format';
import { useOrgScope } from '@/pages/org/OrgLayout';

type Choice = 'default' | 'on' | 'off';

function choiceOf(setting: boolean | null): Choice {
  if (setting === null) return 'default';
  return setting ? 'on' : 'off';
}

function settingOf(choice: Choice): boolean | null {
  if (choice === 'default') return null;
  return choice === 'on';
}

export function FeatureRow({
  orgId,
  feature,
  onSaved,
}: {
  orgId: string;
  feature: AdminOrgFeature;
  onSaved: (next: AdminOrgFeatures) => void;
}) {
  const [choice, setChoice] = useState<Choice>(choiceOf(feature.setting));
  const [note, setNote] = useState(feature.note ?? '');
  const [saving, setSaving] = useState(false);
  const changed = choice !== choiceOf(feature.setting) || (choice !== 'default' && note.trim() !== (feature.note ?? ''));

  const choices: Array<{ id: Choice; label: string }> = [
    { id: 'default', label: `Default (${feature.defaultEnabled ? 'on' : 'off'})` },
    { id: 'on', label: 'On' },
    { id: 'off', label: 'Off' },
  ];

  const save = async () => {
    setSaving(true);
    try {
      const next = await adminClient.putOrgFeature(orgId, feature.feature, {
        enabled: settingOf(choice),
        ...(choice !== 'default' && note.trim() ? { note: note.trim() } : {}),
      });
      onSaved(next);
      toast.success(`${feature.label} saved`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to save ${feature.label}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminSection
      title={feature.label}
      description={feature.description}
      actions={
        <div className="flex items-center gap-2">
          <StatusPill tone={feature.enabled ? 'success' : 'neutral'}>{feature.enabled ? 'On' : 'Off'}</StatusPill>
          <Button type="button" size="sm" onClick={() => void save()} disabled={!changed || saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Save
          </Button>
        </div>
      }
    >
      {!feature.deployEnabled ? (
        <p className="mb-3 text-xs text-warning-foreground">
          Off for every organization on this deploy, whatever is set here.
        </p>
      ) : null}
      <fieldset className="flex flex-wrap gap-2" disabled={saving}>
        <legend className="sr-only">{feature.label} for this organization</legend>
        {choices.map((option) => (
          <label
            key={option.id}
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-muted/40"
          >
            <input
              type="radio"
              name={`feature-${feature.feature}`}
              value={option.id}
              checked={choice === option.id}
              onChange={() => setChoice(option.id)}
            />
            {option.label}
          </label>
        ))}
      </fieldset>
      {choice !== 'default' ? (
        <Input
          className="mt-3"
          aria-label={`Why ${feature.label} is set this way`}
          placeholder="Why (optional), for whoever looks next"
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
        />
      ) : null}
      {feature.updatedAt ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Set {formatDateTime(feature.updatedAt)}
          {feature.updatedByUserId ? ` by ${feature.updatedByUserId}` : ''}
        </p>
      ) : null}
    </AdminSection>
  );
}

/**
 * Studio Admin → an organization → Features: which Studio features (Cowork, Workflows,
 * Agents, …) the organization has. A change reaches every Studio server within seconds.
 */
export function OrgFeaturesPage() {
  const { orgId } = useOrgScope();
  const [features, setFeatures] = useState<AdminOrgFeatures | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFeatures(undefined);
    setError(undefined);
    adminClient
      .getOrgFeatures(orgId)
      .then((next) => {
        if (!cancelled) setFeatures(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load features');
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, reloadKey]);

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Features"
        description="Turn Studio features on or off for this organization. Off hides the feature and refuses new work, scheduled Workflow runs included. Nothing is deleted, so turning it back on restores everything."
      />
      {error ? (
        <LoadError message={error} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : !features ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading features
        </div>
      ) : (
        <div className="space-y-6">
          {features.features.map((feature) => (
            <FeatureRow
              key={`${feature.feature}:${feature.updatedAt ?? 'default'}:${String(feature.setting)}`}
              orgId={orgId}
              feature={feature}
              onSaved={setFeatures}
            />
          ))}
        </div>
      )}
    </div>
  );
}
